// Stale-tab chunk-load recovery.
//
// When we deploy, Vite emits new hashed chunk filenames and the deploy prunes
// the old ones from the server. A browser tab still running a PRE-deploy build
// will ask for a chunk hash that no longer exists; Netlify's SPA fallback then
// returns index.html (text/html) instead of the .js file. The lazy import()
// fails with errors like:
//   - "'text/html' is not a valid JavaScript MIME type"
//   - "Failed to fetch dynamically imported module"
//   - "error loading dynamically imported module"
//   - "Importing a module script failed" (Safari)
//
// This guard catches that case and reloads the page ONCE so the stale tab pulls
// the current build, instead of throwing a red error in the user's face. A
// sessionStorage timestamp prevents a reload loop if the chunk genuinely can't
// load (real network outage / server down).

const RELOAD_STAMP_KEY = "bardin:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 30_000; // don't reload more than once per 30s

const CHUNK_FAILURE_SIGNATURES = [
  "is not a valid JavaScript MIME type",
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "ChunkLoadError",
];

function looksLikeStaleChunk(message: string | undefined | null): boolean {
  if (!message) return false;
  return CHUNK_FAILURE_SIGNATURES.some((sig) => message.includes(sig));
}

function maybeReload(reason: string): void {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) || 0);
  } catch {
    // sessionStorage can throw in private mode / sandboxed iframes — ignore.
  }

  // Already reloaded very recently → the fresh build still can't load this
  // chunk (genuine outage). Stop here so we don't loop.
  if (Date.now() - last < RELOAD_COOLDOWN_MS) {
    console.warn(`[chunkReload] stale-chunk detected again after reload (${reason}) — not reloading to avoid a loop.`);
    return;
  }

  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }

  console.warn(`[chunkReload] stale build detected (${reason}) — reloading to pick up the latest deploy.`);
  // Reload from the server (not cache) so we get the current index.html + chunks.
  window.location.reload();
}

export function installChunkReloadGuard(): void {
  // Vite's own signal that a dynamically-imported chunk failed to preload.
  window.addEventListener("vite:preloadError", (e) => {
    // Prevent Vite from rethrowing; we handle recovery via reload.
    e.preventDefault();
    maybeReload("vite:preloadError");
  });

  // Catch the runtime import() rejections that our component-level try/catch
  // blocks surface (these don't always trigger vite:preloadError).
  window.addEventListener("unhandledrejection", (e) => {
    const msg =
      (e.reason && (e.reason.message as string)) ||
      (typeof e.reason === "string" ? e.reason : "");
    if (looksLikeStaleChunk(msg)) {
      maybeReload("unhandledrejection");
    }
  });

  // Some browsers surface module-script load failures as a window error event.
  window.addEventListener("error", (e) => {
    if (looksLikeStaleChunk(e.message)) {
      maybeReload("window.error");
    }
  });
}
